// The upload rules for menu images: item photos and their gallery, logos,
// sections and slides. The feedback-page link image posts to a different
// endpoint under its own storage prefix and is out of scope here.
//
// The kinds differ only in whether animated media is allowed. Still surfaces
// render with <img>, so a .mp4 there is not a smaller version of the same
// thing - it is a file that cannot display.

export type UploadKind = "still" | "media";

const MAX_BYTES_STILL = 5 * 1024 * 1024; // 5 MB
const MAX_BYTES_GIF = 5 * 1024 * 1024;   // 5 MB
const MAX_BYTES_MP4 = 8 * 1024 * 1024;   // 8 MB, under the backend's 10 MB cap

// .jfif, .jpe, .pjp and .pjpeg are JPEG under a different spelling - Chrome's
// "save image as" writes .jfif on Windows. They are rewritten to .jpeg rather
// than admitted as extensions of their own: storage derives an object's
// Content-Type from its extension, so every extra spelling would need its own
// entry in that map and in the upload validator to avoid being stored as
// binary/octet-stream. An upload keeps only its extension anyway - the server
// renames the file to a UUID - so the rewrite discards nothing.
const JPEG_ALIAS_EXTS = ["jpg", "jfif", "jpe", "pjp", "pjpeg"];

const STILL_EXTS = ["jpeg", "png", "webp"];
const MEDIA_EXTS = [...STILL_EXTS, "gif", "mp4"];

export const ACCEPT_STILL = "image/jpeg,image/png,image/webp,.jfif,.jpe,.pjp,.pjpeg";
export const ACCEPT_MEDIA = ACCEPT_STILL + ",image/gif,video/mp4";

export const acceptFor = (kind: UploadKind): string =>
    kind === "media" ? ACCEPT_MEDIA : ACCEPT_STILL;

export type UploadCheck =
    | { ok: true; file: File; ext: string }
    | { ok: false; message: string };

const maxBytesFor = (ext: string): number => {
    if (ext === "gif") return MAX_BYTES_GIF;
    if (ext === "mp4") return MAX_BYTES_MP4;
    return MAX_BYTES_STILL;
};

// Animation is expressible only through an extended (VP8X) header carrying this
// flag, so the first 21 bytes settle whether a WebP is animated.
const WEBP_ANIMATION_FLAG = 0x02;

// FileReader rather than Blob.arrayBuffer: the latter is absent from the jsdom
// build the test runner uses, and this needs no more than the leading bytes.
const readHead = (file: File, bytes: number): Promise<Uint8Array> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.readAsArrayBuffer(file.slice(0, bytes));
    });

async function isAnimatedWebP(file: File): Promise<boolean> {
    // A file the browser cannot read is not one it can upload either, so the
    // caller learns nothing useful from distinguishing this case.
    const head = await readHead(file, 21).catch(() => new Uint8Array());
    if (head.length < 21) return false;

    const tag = (at: number) => String.fromCharCode(...Array.from(head.subarray(at, at + 4)));
    if (tag(0) !== "RIFF" || tag(8) !== "WEBP" || tag(12) !== "VP8X") return false;

    return (head[20] & WEBP_ANIMATION_FLAG) !== 0;
}

/**
 * Validates a picked file and returns it under the extension the rest of the
 * system should see. The returned File is the one to upload - for a JPEG alias
 * it is a renamed copy, not the original.
 */
export async function checkUpload(selected: File, kind: UploadKind): Promise<UploadCheck> {
    const lastDot = selected.name.lastIndexOf(".");
    const rawExt = lastDot >= 0 ? selected.name.substring(lastDot + 1).toLowerCase() : "";
    const ext = JPEG_ALIAS_EXTS.includes(rawExt) ? "jpeg" : rawExt;

    const allowed = kind === "media" ? MEDIA_EXTS : STILL_EXTS;
    if (!allowed.includes(ext)) {
        return {
            ok: false,
            message: kind === "media"
                ? "Поддерживаются только JPEG, PNG, WebP, GIF и MP4."
                : "Поддерживаются только JPEG, PNG, WebP.",
        };
    }

    const maxBytes = maxBytesFor(ext);
    if (selected.size > maxBytes) {
        return {
            ok: false,
            message: `Файл слишком большой. Максимум — ${maxBytes / (1024 * 1024)} МБ.`,
        };
    }

    // Animated WebP is refused because nothing downstream can reduce it to a
    // still: x/image/webp and ffmpeg both read only the single-image form, so
    // the thumbnailer retries, gives up, and leaves tiles falling back to the
    // original - which then animates a tile meant to be static.
    if (ext === "webp" && await isAnimatedWebP(selected)) {
        return {
            ok: false,
            message: "Анимированный WebP не поддерживается. Сохраните файл как GIF, MP4 или обычный WebP.",
        };
    }

    // The rewrite carries the MIME type as well as the name. The cropper
    // re-encodes to the File's own type, and a .jfif that the OS reports as an
    // empty or vendor-specific type would otherwise be saved as PNG.
    const file = rawExt === ext
        ? selected
        : new File([selected], selected.name.slice(0, lastDot) + "." + ext, {type: "image/jpeg"});

    return {ok: true, file, ext};
}
